<template>
  <div class="quill-editor-wrapper">
    <quill-editor
      ref="quillEditor"
      v-model="content"
      :options="editorOption"
      :style="{ height: height }"
      @blur="onBlur"
      @focus="onFocus"
      @input="onInput"
    ></quill-editor>
    <div class="word-count">
      字数: {{ wordCount }}
    </div>
  </div>
</template>

<script>
import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.snow.css'

export default {
  name: 'QuillEditor',
  components: {
    quillEditor
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    height: {
      type: String,
      default: '500px'
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      content: this.value,
      wordCount: 0,
      editorOption: {
        theme: 'snow',
        readOnly: this.readonly,
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],        // 加粗、斜体、下划线、删除线
            ['blockquote', 'code-block'],                      // 引用、代码块
            [{ 'header': 1 }, { 'header': 2 }, { 'header': 3 }], // 标题1-3
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],     // 有序列表、无序列表
            [{ 'align': '' }, { 'align': 'center' }, { 'align': 'right' }], // 对齐方式
            ['clean'],                                         // 清除格式
            ['link']                                           // 链接
          ]
        }
      }
    }
  },
  watch: {
    value(newVal) {
      this.content = newVal
    },
    readonly(newVal) {
      this.editorOption.readOnly = newVal
    }
  },
  mounted() {
    this.wordCount = this.getWordCount(this.content)
    this.$emit('on-word-count-change', this.wordCount)
  },
  methods: {
    onInput() {
      this.$emit('input', this.content)
      this.wordCount = this.getWordCount(this.content)
      this.$emit('on-word-count-change', this.wordCount)
    },
    onBlur() {
      this.$emit('blur')
    },
    onFocus() {
      this.$emit('focus')
    },
    getWordCount(html) {
      if (!html) return 0
      const text = html.replace(/<[^>]*>/g, '')
      const chineseChars = text.match(/[\u4e00-\u9fa5]/g) || []
      const englishWords = text.match(/[a-zA-Z]+/g) || []
      const numbers = text.match(/\d+/g) || []
      return chineseChars.length + englishWords.length + numbers.length
    },
    // 获取编辑器实例
    getEditorInstance() {
      return this.$refs.quillEditor.quill
    },
    // 清空内容
    clear() {
      this.content = ''
    },
    // 获取纯文本
    getPlainText() {
      const editor = this.getEditorInstance()
      return editor ? editor.getText() : ''
    },
    // 获取HTML内容
    getHTML() {
      return this.content
    }
  }
}
</script>

<style lang="scss" scoped>
.quill-editor-wrapper {
  display: flex;
  flex-direction: column;
  border: 1px solid #e8eaed;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;

  :deep(.ql-container) {
    font-size: 15px;
    line-height: 1.8;
    color: #333;
  }

  :deep(.ql-editor) {
    min-height: 300px;
    padding: 16px;

    h1 {
      font-size: 24px;
      font-weight: 600;
      margin: 20px 0 12px;
      color: #333;
      border-bottom: 2px solid #1890ff;
      padding-bottom: 8px;
    }

    h2 {
      font-size: 20px;
      font-weight: 600;
      margin: 18px 0 10px;
      color: #1890ff;
    }

    h3 {
      font-size: 18px;
      font-weight: 600;
      margin: 16px 0 8px;
      color: #666;
    }

    p {
      margin: 8px 0;
      text-align: justify;
    }

    ul, ol {
      padding-left: 24px;
      margin: 8px 0;
    }

    li {
      margin: 4px 0;
      line-height: 1.8;
    }

    strong {
      font-weight: 600;
      color: #333;
    }

    em {
      font-style: italic;
    }

    u {
      text-decoration: underline;
    }

    blockquote {
      border-left: 4px solid #1890ff;
      padding-left: 12px;
      margin: 12px 0;
      color: #666;
      background: #f8f9fa;
      padding: 8px 12px;
    }

    code {
      background: #f4f4f4;
      padding: 2px 6px;
      border-radius: 3px;
      font-family: 'Monaco', 'Menlo', monospace;
      font-size: 14px;
    }

    .ql-align-center {
      text-align: center;
    }

    .ql-align-right {
      text-align: right;
    }
  }

  :deep(.ql-toolbar) {
    background: #fafafa;
    border-bottom: 1px solid #e8eaed;
  }

  :deep(.ql-toolbar button) {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #666;
    transition: all 0.2s;

    &:hover {
      color: #1890ff;
    }

    &.ql-active {
      color: #1890ff;
    }
  }

  :deep(.ql-toolbar .ql-stroke) {
    fill: none;
    stroke: currentColor;
  }

  :deep(.ql-toolbar .ql-fill) {
    fill: currentColor;
    stroke: none;
  }

  :deep(.ql-toolbar .ql-picker-label) {
    color: #666;
  }
}

.word-count {
  padding: 8px 12px;
  background: #fafafa;
  border-top: 1px solid #e8eaed;
  font-size: 12px;
  color: #666;
  text-align: right;
}
</style>